import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Image, ScrollView, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import type { Href } from 'expo-router';
import { useGlobalSearchParams, useRouter, useSegments } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Clipboard from 'expo-clipboard';
import Svg, { Circle, ClipPath, Defs, Image as SvgImage, Line, Path, Rect, Text as SvgText } from 'react-native-svg';
import { useTheme } from '@/lib/styling';
import {
  ArrowDown,
  ArrowUp,
  Bell,
  ChevronRight,
  Copy,
  Search,
  ShieldAlert,
  Sparkles,
  Trophy,
  Undo2,
  User,
  UserPlus,
  Users,
} from 'lucide-react-native';
import { Theme } from '@/theme';
import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AppSelect } from '@/components/ui/AppSelect';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/icon';
import { Avatar, AvatarImage, AvatarFallback, GradientAvatarFallback, resolveAvatarUri } from '@/components/ui/Avatar';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { useTRPC, useTRPCClient } from '@/lib/trpc';
import { isUnauthorizedError } from '@/lib/authUtils';
import { clientLogger } from '@/lib/datadog';
import { notificationPresetOptions } from '@/lib/notificationPresets';
import { getLeagueHref, type LeaguePrimarySection } from '@/lib/routes';
import { MMClockBanner, MMRankBadge, MMDraftHeader } from './MarchMadnessUI';
import { MarchMadnessBracketView } from './MarchMadnessBracketView';
import { useLeagueContext } from '@/contexts/LeagueContext';

const NCAA_BLUE = '#005EB8';
const MM_SCORE_PILL_WIDTH = 280;
const MM_SCORE_PILL_PRIMARY_WIDTH = 140;
const MM_SCORE_PILL_SECONDARY_WIDTH = MM_SCORE_PILL_WIDTH - MM_SCORE_PILL_PRIMARY_WIDTH - 1;
const MM_SCORE_RACE_MIN_WIDTH = 850;
const MM_SCORE_RACE_HEIGHT = 320;
const MM_SCORE_RACE_ROUND_BAND_WIDTH = 280;
const SCORE_RACE_COLORS = ['#005EB8', '#FF6B00', '#0F9D58', '#D7263D', '#7C3AED', '#00A6A6', '#A16207', '#334155'];
const SCORE_RACE_CLOSE_SCORE_DELTA = 3;
const SCORE_RACE_ENDPOINT_SPREAD = 52;

// Reimagined Score Climber Constants
const MOUNTAIN_GRAY_DARK = '#64748B';
const COURT_LINE_COLOR = 'rgba(15, 23, 42, 0.05)'; // Very subtle dark grid

const MMBasketballMarker = ({ cx, cy, size = 10 }: { cx: number, cy: number, size?: number }) => (
  <React.Fragment>
    {/* Basketball Shadow */}
    <Circle cx={cx + 1} cy={cy + 1} r={size / 2} fill="rgba(0,0,0,0.2)" />
    {/* Ball Base */}
    <Circle cx={cx} cy={cy} r={size / 2} fill="#FF6B00" stroke="#000" strokeWidth={size / 12} />
    {/* Ball Lines */}
    <Path
      d={`M ${cx - size * 0.4} ${cy} Q ${cx} ${cy - size * 0.2} ${cx + size * 0.4} ${cy}
         M ${cx} ${cy - size * 0.4} Q ${cx - size * 0.2} ${cy} ${cx} ${cy + size * 0.4}
         M ${cx - size * 0.3} ${cy - size * 0.3} Q ${cx} ${cy} ${cx + size * 0.3} ${cy + size * 0.3}
         M ${cx + size * 0.3} ${cy - size * 0.3} Q ${cx} ${cy} ${cx - size * 0.3} ${cy + size * 0.3}`}
      stroke="#000"
      strokeWidth={size / 15}
      fill="none"
    />
  </React.Fragment>
);

type LeaguePermissions = {
  isOwner?: boolean;
  isManager?: boolean;
};

type MarchMadnessLeagueViewProps = {
  leagueId: string;
  seasonId: string;
  currentUserId: string | undefined;
  permissions: LeaguePermissions | undefined;
  section?: LeaguePrimarySection;
};

type DraftState = {
  draft: {
    id: string;
    status: 'pending' | 'active' | 'paused' | 'completed';
    currentPickNumber: number;
    currentDrafterLeagueMemberId: string | null;
    rosterSize: number;
  };
  draftOrder: string[];
  members: {
    leagueMemberId: string;
    userId: string;
    displayName: string;
    profileImageUrl: string | null;
  }[];
  rosters: Record<string, string[]>;
  availablePlayers: {
    id: string;
    fullName: string;
    teamName: string | null;
    teamAbbreviation: string | null;
    position: string | null;
  }[];
  picks: {
    id: string;
    leagueMemberId: string;
    drafterDisplayName: string;
    playerId: string;
    playerName: string | null;
    playerTeamAbbreviation: string | null;
    playerTeamName: string | null;
    playerPosition: string | null;
    pickNumber: number;
    roundNumber: number;
  }[];
};

type Overview = {
  draft: {
    id: string;
    status: 'pending' | 'active' | 'paused' | 'completed';
    rosterSize: number;
    completedAt: string | null;
  };
  rounds: {
    roundKey: string;
    roundOrder: number;
    label: string;
    totalGames: number;
    finalGames: number;
    isFinal: boolean;
  }[];
  standings: {
    leagueMemberId: string;
    userId: string;
    displayName: string;
    profileImageUrl: string | null;
    rank: number;
    totalPoints: number;
    roundTotals: Record<string, number>;
    roster: {
      playerId: string;
      fullName: string;
      teamId: string | null;
      teamName: string | null;
      teamAbbreviation: string | null;
      teamLogoUrl: string | null;
      position: string | null;
      pickNumber: number;
      draftRoundNumber: number;
      totalPoints: number;
      roundPoints: Record<string, number>;
      isEliminated?: boolean;
      isLive?: boolean;
    }[];
    }[];
  winner: {
    status: 'pending' | 'final';
    isTie: boolean;
    leagueMemberIds: string[];
    totalPoints: number | null;
  };
  picks: DraftState['picks'];
};

type MarchMadnessTab = 'draft' | 'standings' | 'rosters' | 'admin' | 'bracket';

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

function LiveIndicator({ label }: { label: string }) {
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 0.6,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [pulseAnim]);

  return (
    <Animated.View style={[{ opacity: pulseAnim }]}>
      <Badge variant="success" paddingHorizontal={4} paddingVertical={0} height={16}>
        <Box flexDirection="row" alignItems="center" gap="xs">
          <Box width={4} height={4} backgroundColor="successForeground" />
          <Text variant="caption" color="successForeground" fontWeight="800" fontSize={8}>
            {label.toUpperCase()}
          </Text>
        </Box>
      </Badge>
    </Animated.View>
  );
}

const getSortableTeamName = (teamName: string | null, teamAbbreviation: string | null) =>
  (teamName ?? teamAbbreviation ?? '').trim();

const getSortableLastName = (fullName: string | null | undefined) => {
  const name = fullName?.trim() ?? '';
  if (!name) return '';
  const parts = name.split(/\s+/);
  return parts[parts.length - 1] ?? name;
};

const normalizeValue = (value: string) => value.toLowerCase().trim();

const getInitials = (displayName: string) =>
  displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const isSubsequenceMatch = (needle: string, haystack: string) => {
  if (!needle) return true;
  let needleIndex = 0;
  for (let index = 0; index < haystack.length && needleIndex < needle.length; index += 1) {
    if (haystack[index] === needle[needleIndex]) needleIndex += 1;
  }
  return needleIndex === needle.length;
};

const getPlayerSearchRank = (
  player: {
    fullName: string;
    teamName: string | null;
    teamAbbreviation: string | null;
    position: string | null;
  },
  query: string,
) => {
  if (!query) return 0;

  const normalizedFullName = normalizeValue(player.fullName);
  const normalizedTeamName = normalizeValue(player.teamName ?? '');
  const normalizedTeamAbbreviation = normalizeValue(player.teamAbbreviation ?? '');
  const normalizedPosition = normalizeValue(player.position ?? '');
  const combined = `${normalizedFullName} ${normalizedTeamAbbreviation} ${normalizedTeamName} ${normalizedPosition}`.trim();
  const tokens = query.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) return 0;
  if (!tokens.every((token) => combined.includes(token) || isSubsequenceMatch(token, combined))) {
    return null;
  }

  if (normalizedTeamName === query || normalizedTeamAbbreviation === query || normalizedFullName === query) return 1;
  if (normalizedTeamName.startsWith(query) || normalizedTeamAbbreviation.startsWith(query)) return 2;
  if (normalizedFullName.startsWith(query)) return 3;
  if (tokens.every((token) => normalizedTeamName.split(/\s+/).some((word) => word.startsWith(token)))) return 4;
  if (tokens.every((token) => normalizedFullName.split(/\s+/).some((word) => word.startsWith(token)))) return 5;
  if (normalizedTeamName.includes(query) || normalizedTeamAbbreviation.includes(query)) return 6;
  if (normalizedFullName.includes(query)) return 7;
  if (tokens.every((token) => normalizedTeamName.includes(token) || normalizedTeamAbbreviation.includes(token))) return 8;
  if (tokens.every((token) => normalizedFullName.includes(token))) return 9;
  return 10;
};

const formatWinnerText = (overview: Overview | undefined) => {
  if (!overview) return null;
  if (overview.winner.totalPoints === null || overview.standings.length === 0) return null;

  const winnerNames = overview.standings
    .filter((entry) => overview.winner.leagueMemberIds.includes(entry.leagueMemberId))
    .map((entry) => entry.displayName);
  if (winnerNames.length === 0) return null;

  const base = overview.winner.isTie ? `${winnerNames.join(', ')} tied at ${overview.winner.totalPoints}` : `${winnerNames[0]} leads with ${overview.winner.totalPoints}`;
  return overview.winner.status === 'final' ? `${base} points` : `${base} points so far`;
};

export default function MarchMadnessLeagueView({
  leagueId,
  seasonId,
  currentUserId,
  permissions,
  section,
}: MarchMadnessLeagueViewProps) {
  const router = useRouter();
  const context = useLeagueContext();
  const {
    league,
    leagueSeasons,
    mmStatus: statusData,
    mmDraftState: draftState,
    mmOverview: overview,
    mmBracket: bracket,
    mmSelectedRoundDetail: selectedRoundDetail,
    mmSelectedRoundKey: selectedRoundKey,
  } = context;

  const routeParams = useGlobalSearchParams<{
    season?: string | string[];
    week?: string | string[];
    mmRound?: string | string[];
  }>();
  const mmRoundParam = Array.isArray(routeParams.mmRound) ? routeParams.mmRound[0] : routeParams.mmRound;
  const seasonParam = Array.isArray(routeParams.season) ? routeParams.season[0] : routeParams.season;
  const weekParam = Array.isArray(routeParams.week) ? routeParams.week[0] : routeParams.week;
  const { width: screenWidth } = useWindowDimensions();
  const theme = useTheme<Theme>();
  const draftScrollStyle = { flex: 1 };
  const draftScrollContentStyle = { gap: theme.spacing.m, paddingBottom: 40 };
  const draftContentGapStyle = { gap: 12 };
  const draftHeaderStyle = { paddingBottom: 8 };
  const draftBoardContentStyle = { paddingHorizontal: 0 };
  const playerSearchStyle = { paddingLeft: 36 };
  const playerFinderContentStyle = { paddingHorizontal: 0 };
  const playerFinderScrollStyle = { maxHeight: 450 };
  const playerFinderHeaderStyle = { paddingBottom: 16 };
  const bracketScrollStyle = { flex: 1 };
  const bracketLoadingStyle = { paddingVertical: 32, alignItems: 'center' as const, justifyContent: 'center' as const };
  const standingsScrollStyle = { flex: 1 };
  const standingsContentStyle = { padding: theme.spacing.m, paddingBottom: 24, gap: theme.spacing.m };
  const winningStandingStyle = { shadowColor: theme.colors.press, shadowOffset: { width: 3, height: 3 }, shadowOpacity: 1, shadowRadius: 0, elevation: 0 };
  const scoreRaceContentStyle = { paddingBottom: 20 };
  const rostersScrollStyle = { flex: 1 };
  const rostersScrollContentStyle = { gap: theme.spacing.m, paddingBottom: 40 };
  const adminContentGapStyle = { gap: 12 };
  const recapPromptStyle = { minHeight: 220 };
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const mmInput = { leagueId, seasonId: seasonId ?? '' };
  const canManage = Boolean(permissions?.isOwner || permissions?.isManager);
  const [manualPlayerName, setManualPlayerName] = useState('');
  const [manualPlayerPosition, setManualPlayerPosition] = useState('');
  const [manualPlayerTeamName, setManualPlayerTeamName] = useState('');
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [overrideCurrentPickMode, setOverrideCurrentPickMode] = useState(false);
  const [editableDraftOrder, setEditableDraftOrder] = useState<string[]>([]);
  const [recapPrompt, setRecapPrompt] = useState('');
  const [recapTone, setRecapTone] = useState<'fun' | 'competitive' | 'chaotic' | 'professional'>('fun');
  const [notificationPreset, setNotificationPreset] = useState(notificationPresetOptions[0]!.value);
  const [notificationMessage, setNotificationMessage] = useState(notificationPresetOptions[0]!.message);
  const [notificationTitle, setNotificationTitle] = useState(notificationPresetOptions[0]!.title);
  const [compactBracketRoundKey, setCompactBracketRoundKey] = useState<string | null>(null);
  const notificationMessageLimit = 200;
  const notificationTitleLimit = 80;
  const activeSeasonId = useMemo(() => {
    const explicitActiveSeason = leagueSeasons?.find((season: any) => season?.isActive)?.id;
    return explicitActiveSeason ?? league?.currentSeason?.id ?? league?.resolvedSeasonId ?? null;
  }, [league?.currentSeason?.id, league?.resolvedSeasonId, leagueSeasons]);
  
  const buildMmHref = useCallback(
    (targetSection: LeaguePrimarySection, overrides?: { mmRound?: string | null }) => {
      const searchParams = new URLSearchParams();

      const requestedSeasonId = seasonParam ?? seasonId;
      if (requestedSeasonId && requestedSeasonId !== 'active' && requestedSeasonId !== activeSeasonId) {
        searchParams.set('season', requestedSeasonId);
      }
      
      const activeWeekId = weekParam;
      if (activeWeekId) {
        searchParams.set('week', activeWeekId);
      }

      if (overrides?.mmRound) {
        searchParams.set('mmRound', overrides.mmRound);
      }

      const queryString = searchParams.toString();
      const basePath = String(getLeagueHref(leagueId, targetSection));
      return (queryString.length > 0 ? `${basePath}?${queryString}` : basePath) as Href;
    },
    [activeSeasonId, leagueId, seasonParam, weekParam, seasonId],
  );

  const segments = useSegments();

  const defaultRouteSection: LeaguePrimarySection = draftState?.draft?.status === 'completed' ? 'standings' : 'draft';
  const routeSection: LeaguePrimarySection =
    section === 'bracket' ||
    section === 'standings' ||
    section === 'rosters' ||
    section === 'draft' ||
    (section === 'admin' && canManage)
      ? section
      : defaultRouteSection;
  const activeTab: MarchMadnessTab =
    routeSection === 'admin' && !canManage ? defaultRouteSection : (routeSection as MarchMadnessTab);

  const invalidateMarchMadnessQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.marchMadness.status.queryFilter(mmInput)),
      queryClient.invalidateQueries(trpc.marchMadness.draft.queryFilter(mmInput)),
      queryClient.invalidateQueries(trpc.marchMadness.overview.queryFilter(mmInput)),
      queryClient.invalidateQueries(trpc.marchMadness.bracket.queryFilter(mmInput)),
      queryClient.invalidateQueries(trpc.marchMadness.round.queryFilter()),
    ]);
  };

  const roundSelectorRows = useMemo(() => {
    const rounds = overview?.rounds ?? [];
    if (screenWidth >= 900 || rounds.length <= 4) {
      return [rounds];
    }

    const firstRowCount = Math.ceil(rounds.length / 2);
    return [rounds.slice(0, firstRowCount), rounds.slice(firstRowCount)];
  }, [overview?.rounds, screenWidth]);

  const cumulativePointsByMemberPlayer = useMemo(() => {
    if (!overview?.standings?.length || !overview?.rounds?.length || !selectedRoundKey) {
      return new Map<string, number>();
    }

    const selectedRound = overview.rounds.find((round: any) => round.roundKey === selectedRoundKey);
    if (!selectedRound) {
      return new Map<string, number>();
    }

    const includedRoundKeys = overview.rounds
      .filter(
        (round: any) =>
          round.roundOrder < selectedRound.roundOrder ||
          (round.roundOrder === selectedRound.roundOrder && round.roundKey <= selectedRound.roundKey),
      )
      .map((round: any) => round.roundKey);

    const pointsMap = new Map<string, number>();
    for (const member of overview.standings) {
      for (const player of member.roster) {
        const cumulativePoints = includedRoundKeys.reduce((sum: number, roundKey: string) => sum + (player.roundPoints[roundKey] ?? 0), 0);
        pointsMap.set(`${member.leagueMemberId}:${player.playerId}`, cumulativePoints);
      }
    }

    return pointsMap;
  }, [overview?.rounds, overview?.standings, selectedRoundKey]);

  const maxTournamentPoints = useMemo(
    () => Math.max(...(overview?.standings?.map((entry: any) => entry.totalPoints) ?? [1]), 1),
    [overview?.standings],
  );

  const scoreRaceSeries = useMemo(() => {
    const allRounds = overview?.rounds ?? [];
    const standings = overview?.standings ?? [];
    const activeRoundIndex = allRounds.findIndex((round: any) => !round.isFinal);
    const visibleRounds =
      activeRoundIndex >= 0
        ? allRounds.slice(0, activeRoundIndex + 1)
        : allRounds;

    return standings.map((entry: any, index: number) => {
      let cumulativeScore = 0;
      const points = visibleRounds.map((round: any) => {
          cumulativeScore += entry.roundTotals[round.roundKey] ?? 0;
          return {
            key: round.roundKey,
            label: round.label,
            cumulativeScore,
          };
        });

      return {
        leagueMemberId: entry.leagueMemberId,
        displayName: entry.displayName,
        profileImageUrl: entry.profileImageUrl,
        rank: entry.rank,
        totalPoints: entry.totalPoints,
        color: SCORE_RACE_COLORS[index % SCORE_RACE_COLORS.length] ?? NCAA_BLUE,
        points,
      };
    });
  }, [overview?.rounds, overview?.standings]);

  const scoreRaceChart = useMemo(() => {
    const horizontalPadding = 52;
    const extraRightPadding = 80; // More room for the big hoop at the end
    const topPadding = 20;
    const bottomPadding = 34;
    const rounds = overview?.rounds ?? [];
    const naturalChartWidth = horizontalPadding + extraRightPadding + MM_SCORE_RACE_ROUND_BAND_WIDTH * Math.max(rounds.length, 1);
    const availableChartWidth = Math.max(screenWidth - 96, MM_SCORE_RACE_MIN_WIDTH);
    const chartWidth = Math.max(MM_SCORE_RACE_MIN_WIDTH, Math.min(availableChartWidth, naturalChartWidth));
    const chartHeight = MM_SCORE_RACE_HEIGHT;
    const innerWidth = chartWidth - horizontalPadding - extraRightPadding;
    const innerHeight = chartHeight - topPadding - bottomPadding;
    const roundCount = Math.max(rounds.length - 1, 1);
    const valueMax = Math.max(maxTournamentPoints, 1);

    const gridValues = Array.from({ length: 5 }, (_, index) => {
      const ratio = index / 4;
      return Math.round(valueMax - valueMax * ratio);
    });

    const xForIndex = (index: number) => horizontalPadding + (innerWidth / roundCount) * index;
    const yForValue = (value: number) => topPadding + innerHeight - (value / valueMax) * innerHeight;
    const roundBandBounds = rounds.map((round: any, index: number) => {
      const centerX = xForIndex(index);
      const previousCenterX = index > 0 ? xForIndex(index - 1) : null;
      const nextCenterX = index < rounds.length - 1 ? xForIndex(index + 1) : null;
      const startX = previousCenterX === null ? horizontalPadding : (previousCenterX + centerX) / 2;
      const endX = nextCenterX === null ? chartWidth - extraRightPadding : (centerX + nextCenterX) / 2;

      return {
        key: round.roundKey,
        startX,
        endX,
        centerX: xForIndex(index),
      };
    });

    const series = scoreRaceSeries.map((entry: any) => {
      const points = entry.points.map((point: any, index: number) => ({
        ...point,
        x: xForIndex(index),
        y: yForValue(point.cumulativeScore),
      }));

      let path = '';
      let fillPath = '';
      points.forEach((point: any, index: number) => {
        if (index === 0) {
          path = `M ${point.x} ${point.y}`;
          fillPath = `M ${point.x} ${chartHeight - bottomPadding} L ${point.x} ${point.y}`;
          return;
        }

        // Sloped jump: stay flat until 30% of the way to next round, then slope up
        const prevPoint = points[index - 1];
        const midX = prevPoint.x + (point.x - prevPoint.x) * 0.3;
        
        path += ` H ${midX} L ${point.x} ${point.y}`;
        fillPath += ` H ${midX} L ${point.x} ${point.y}`;

        if (index === points.length - 1) {
          fillPath += ` L ${point.x} ${chartHeight - bottomPadding} Z`;
        }
      });

      return {
        ...entry,
        path,
        fillPath,
        points,
      };
    });

    const endpointOffsets = new Map<string, number>();
    const endpointEntries = series
      .map((entry: any) => ({
        leagueMemberId: entry.leagueMemberId,
        cumulativeScore: entry.points[entry.points.length - 1]?.cumulativeScore ?? 0,
      }))
      .sort((a: any, b: any) => a.cumulativeScore - b.cumulativeScore);

    let groupStartIndex = 0;
    while (groupStartIndex < endpointEntries.length) {
      let groupEndIndex = groupStartIndex + 1;
      while (
        groupEndIndex < endpointEntries.length &&
        endpointEntries[groupEndIndex]!.cumulativeScore - endpointEntries[groupEndIndex - 1]!.cumulativeScore <=
          SCORE_RACE_CLOSE_SCORE_DELTA
      ) {
        groupEndIndex += 1;
      }

      const group = endpointEntries.slice(groupStartIndex, groupEndIndex);
      if (group.length > 1) {
        const step = SCORE_RACE_ENDPOINT_SPREAD / Math.max(group.length - 1, 1);
        group.forEach((entry: any, index: number) => {
          endpointOffsets.set(entry.leagueMemberId, -SCORE_RACE_ENDPOINT_SPREAD / 2 + step * index);
        });
      }

      groupStartIndex = groupEndIndex;
    }

    return {
      chartHeight,
      chartWidth,
      endpointOffsets,
      gridValues,
      horizontalPadding,
      innerHeight,
      innerWidth,
      roundCount,
      roundBandBounds,
      series,
      topPadding,
      xForIndex,
      yForValue,
    };
  }, [maxTournamentPoints, overview?.rounds, scoreRaceSeries, screenWidth]);

  useEffect(() => {
    if (activeTab !== 'bracket') {
      setCompactBracketRoundKey(null);
      return;
    }
    if (screenWidth >= 820) {
      setCompactBracketRoundKey(null);
      return;
    }

    if (!bracket?.rounds?.length) {
      return;
    }

    const compactRoundKeys = new Set<string>(bracket.rounds.map((round: any) => round.roundKey));
    if (bracket.firstFourGames?.length) {
      compactRoundKeys.add('first-four');
    }

    if (compactBracketRoundKey && compactRoundKeys.has(compactBracketRoundKey)) {
      return;
    }

    const defaultRoundKey =
      (selectedRoundKey && bracket.rounds.find((round: any) => round.roundKey === selectedRoundKey)?.roundKey) ??
      bracket.rounds[0]?.roundKey ??
      null;

    if (defaultRoundKey !== compactBracketRoundKey) {
      setCompactBracketRoundKey(defaultRoundKey);
    }
  }, [activeTab, bracket?.firstFourGames?.length, bracket?.rounds, compactBracketRoundKey, screenWidth, selectedRoundKey]);

  const currentSegment = segments[segments.length - 1];
  const isVisibleTab = currentSegment === section;

  useEffect(() => {
    if (!isVisibleTab) return;
    if (!overview?.rounds?.length || !selectedRoundKey) {
      return;
    }
    if (mmRoundParam === selectedRoundKey) {
      return;
    }

    router.replace(buildMmHref(routeSection, { mmRound: selectedRoundKey }));
  }, [isVisibleTab, buildMmHref, mmRoundParam, overview?.rounds, routeSection, router, selectedRoundKey]);

  useEffect(() => {
    setEditableDraftOrder(draftState?.draftOrder ?? []);
  }, [draftState?.draftOrder]);

  const startDraftMutation = useMutation({
    mutationFn: async () => trpcClient.marchMadness.startDraft.mutate(mmInput),
    onSuccess: async () => {
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Draft Started', text2: 'Snake draft is now active.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to start draft.') });
    },
  });

  const makePickMutation = useMutation({
    mutationFn: async (playerId: string) =>
      trpcClient.marchMadness.pick.mutate({ ...mmInput, playerId }),
    onSuccess: async () => {
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Pick Submitted', text2: 'Your draft pick was recorded.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to submit draft pick.') });
    },
  });

  const overridePickMutation = useMutation({
    mutationFn: async (playerId: string) =>
      trpcClient.marchMadness.overridePick.mutate({ ...mmInput, playerId }),
    onSuccess: async () => {
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Override Pick Submitted', text2: 'Pick recorded for current drafter.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to submit override pick.') });
    },
  });

  const undoLastPickMutation = useMutation({
    mutationFn: async () => trpcClient.marchMadness.undoLastPick.mutate(mmInput),
    onSuccess: async () => {
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Pick Undone', text2: 'Last draft pick has been removed.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to undo last pick.') });
    },
  });

  const addManualPlayerMutation = useMutation({
    mutationFn: async () =>
      trpcClient.marchMadness.addManualPlayer.mutate({
        ...mmInput,
        fullName: manualPlayerName.trim(),
        position: manualPlayerPosition.trim() || undefined,
        teamName: manualPlayerTeamName.trim() || undefined,
      }),
    onSuccess: async () => {
      setManualPlayerName('');
      setManualPlayerPosition('');
      setManualPlayerTeamName('');
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Player Added', text2: 'Manual player added to draft pool.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to add manual player.') });
    },
  });

  const updateDraftOrderMutation = useMutation({
    mutationFn: async (orderedLeagueMemberIds: string[]) =>
      trpcClient.marchMadness.setDraftOrder.mutate({ ...mmInput, orderedLeagueMemberIds }),
    onSuccess: async () => {
      await invalidateMarchMadnessQueries();
      toast.show({ type: 'success', text1: 'Draft Order Updated', text2: 'The saved draft order has been updated.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to update draft order.') });
    },
  });

  const generateRecapPromptMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRoundKey) {
        throw new Error('Choose a round first.');
      }
      return trpcClient.marchMadness.adminRecap.query({
        ...mmInput,
        roundKey: selectedRoundKey,
        tone: recapTone,
      });
    },
    onSuccess: (data: { prompt?: string }) => {
      const prompt = typeof data?.prompt === 'string' ? data.prompt : '';
      if (!prompt) {
        toast.show({ type: 'error', text1: 'Missing prompt', text2: 'No recap prompt was returned.' });
        return;
      }
      setRecapPrompt(prompt);
      toast.show({ type: 'success', text1: 'Prompt generated', text2: 'Recap prompt is ready to copy.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to generate recap prompt.') });
    },
  });

  const sendNotificationMutation = useMutation({
    mutationFn: async () =>
      trpcClient.marchMadness.adminNotify.mutate({
        ...mmInput,
        title: notificationTitle.trim() || 'League Update',
        message: notificationMessage.trim(),
      }),
    onSuccess: () => {
      toast.show({ type: 'success', text1: 'Notification sent', text2: 'Manual push sent to league members.' });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({ type: 'error', text1: 'Unauthorized', text2: 'Please log in again.' });
        return;
      }
      toast.show({ type: 'error', text1: 'Error', text2: getErrorMessage(error, 'Failed to send notification.') });
    },
  });

  const copyRecapPrompt = async () => {
    if (!recapPrompt.trim()) {
      toast.show({ type: 'info', text1: 'Generate prompt first', text2: 'Create a recap prompt before copying it.' });
      return;
    }
    try {
      await Clipboard.setStringAsync(recapPrompt);
      toast.show({ type: 'success', text1: 'Copied', text2: 'Recap prompt copied to clipboard.' });
    } catch (error) {
      toast.show({ type: 'error', text1: 'Copy failed', text2: getErrorMessage(error, 'Failed to copy prompt.') });
    }
  };

  const myLeagueMemberId = useMemo(
    () => draftState?.members.find((member: any) => member.userId === currentUserId)?.leagueMemberId ?? null,
    [draftState?.members, currentUserId],
  );
  const onTheClock = draftState?.draft?.currentDrafterLeagueMemberId === myLeagueMemberId;
  const isDraftPending = draftState?.draft?.status === 'pending';
  const isDraftActive = draftState?.draft?.status === 'active';
  const isDraftCompleted = draftState?.draft?.status === 'completed';
  const orderedMembers = useMemo(() => {
    const members = draftState?.members ?? [];
    const draftOrder = draftState?.draftOrder ?? [];
    if (draftOrder.length !== members.length) return members;

    const membersById = new Map(members.map((member: any) => [member.leagueMemberId, member]));
    const ordered = draftOrder
      .map((leagueMemberId: any) => membersById.get(leagueMemberId))
      .filter((member: any): member is any => Boolean(member));
    return ordered.length === members.length ? ordered : members;
  }, [draftState?.draftOrder, draftState?.members]);
  const memberCount = orderedMembers.length;
  const rosterSize = draftState?.draft?.rosterSize ?? overview?.draft?.rosterSize ?? 0;
  const draftRounds = useMemo(() => Array.from({ length: Math.max(0, rosterSize) }, (_, index) => index + 1), [rosterSize]);
  const pickByMemberRound = useMemo(() => {
    const map = new Map<string, any>();
    for (const pick of draftState?.picks ?? []) {
      map.set(`${pick.leagueMemberId}:${pick.roundNumber}`, pick);
    }
    return map;
  }, [draftState?.picks]);
  const onTheClockDisplayName = useMemo(() => {
    const currentMemberId = draftState?.draft?.currentDrafterLeagueMemberId;
    if (!currentMemberId) return null;
    return orderedMembers.find((member: any) => member.leagueMemberId === currentMemberId)?.displayName ?? null;
  }, [draftState?.draft?.currentDrafterLeagueMemberId, orderedMembers]);
  const currentRoundNumber = useMemo(() => {
    if (!memberCount) return null;
    const pickNumber = draftState?.draft?.currentPickNumber ?? 0;
    if (pickNumber <= 0) return null;
    return Math.floor((pickNumber - 1) / memberCount) + 1;
  }, [draftState?.draft?.currentPickNumber, memberCount]);

  const editableDraftOrderMembers = useMemo(() => {
    const membersById = new Map((draftState?.members ?? []).map((member: any) => [member.leagueMemberId, member]));
    return editableDraftOrder
      .map((leagueMemberId: any) => membersById.get(leagueMemberId))
      .filter((member: any): member is any => Boolean(member));
  }, [draftState?.members, editableDraftOrder]);
  const isDraftOrderDirty = editableDraftOrder.join('|') !== (draftState?.draftOrder ?? []).join('|');
  const moveDraftOrderMember = (leagueMemberId: string, direction: 'up' | 'down') => {
    setEditableDraftOrder((currentOrder) => {
      const currentIndex = currentOrder.indexOf(leagueMemberId);
      if (currentIndex < 0) return currentOrder;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= currentOrder.length) return currentOrder;

      const nextOrder = [...currentOrder];
      const [movedMemberId] = nextOrder.splice(currentIndex, 1);
      if (!movedMemberId) return currentOrder;
      nextOrder.splice(targetIndex, 0, movedMemberId);
      return nextOrder;
    });
  };

  const draftedByPlayerId = useMemo(() => {
    const map = new Map<string, { drafterDisplayName: string; pickNumber: number }>();
    for (const pick of draftState?.picks ?? []) {
      map.set(pick.playerId, { drafterDisplayName: pick.drafterDisplayName, pickNumber: pick.pickNumber });
    }
    return map;
  }, [draftState?.picks]);

  const allPlayers = useMemo(() => {
    const available = (draftState?.availablePlayers ?? []).map((player: any) => ({ ...player, isDrafted: false }));
    const drafted = (draftState?.picks ?? [])
      .filter((pick: any) => pick.playerId && pick.playerName)
      .map((pick: any) => ({
        id: pick.playerId,
        fullName: pick.playerName ?? 'Unknown player',
        teamName: pick.playerTeamName,
        teamAbbreviation: pick.playerTeamAbbreviation,
        position: pick.playerPosition,
        isDrafted: true,
      }));

    const byId = new Map<string, any>();
    [...available, ...drafted].forEach((player) => {
      if (!byId.has(player.id)) {
        byId.set(player.id, player);
      }
    });
    return Array.from(byId.values());
  }, [draftState?.availablePlayers, draftState?.picks]);

  const positionOptions = useMemo(() => {
    const positions = Array.from(
      new Set(allPlayers.map((player: any) => player.position?.toUpperCase().trim()).filter(Boolean) as string[]),
    ).sort((a, b) => a.localeCompare(b));
    return [{ value: 'all', label: 'All positions' }, ...positions.map((position) => ({ value: position, label: position }))];
  }, [allPlayers]);

  const filteredPlayers = useMemo(() => {
    const query = normalizeValue(playerSearchQuery);
    return allPlayers
      .filter((player: any) => positionFilter === 'all' || (player.position ?? '').toUpperCase().trim() === positionFilter)
      .map((player: any) => ({
        player,
        rank: getPlayerSearchRank(player, query),
      }))
      .filter((entry: any) => entry.rank !== null)
      .sort((a: any, b: any) => {
        const rankComparison = (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER);
        if (rankComparison !== 0) return rankComparison;

        const teamComparison = collator.compare(
          getSortableTeamName(a.player.teamName, a.player.teamAbbreviation),
          getSortableTeamName(b.player.teamName, b.player.teamAbbreviation),
        );
        if (teamComparison !== 0) return teamComparison;

        const lastNameComparison = collator.compare(getSortableLastName(a.player.fullName), getSortableLastName(b.player.fullName));
        if (lastNameComparison !== 0) return lastNameComparison;

        return collator.compare(a.player.fullName, b.player.fullName);
      })
      .map((entry: any) => entry.player);
  }, [allPlayers, playerSearchQuery, positionFilter]);

  const winnerText = formatWinnerText(overview);

  const handleNotificationPresetChange = (value: string) => {
    setNotificationPreset(value);
    const preset = notificationPresetOptions.find((option) => option.value === value);
    setNotificationTitle(preset?.title ?? 'League Update');
    setNotificationMessage(preset?.message ?? '');
  };

  if (__DEV__) {
    clientLogger.debug('[MarchMadnessLeagueView] active context', { leagueId, seasonId, section });
  }

  if (!seasonId || !statusData) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" paddingVertical="xl">
        <ActivityIndicator size="small" color={theme.colors.press} />
      </Box>
    );
  }

  // Only block the whole screen if we have absolutely no core tournament data yet
  if (!overview && !draftState && context.isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" paddingVertical="xl">
        <ActivityIndicator size="large" color={theme.colors.press} />
        <Text variant="body" color="mutedForeground" marginTop="m">Loading Tournament Data…</Text>
      </Box>
    );
  }

  if (!statusData?.hasSetup) {
    return (
      <Card marginBottom="m" borderColor="warning" borderWidth={2}>
        <CardHeader>
          <Box flexDirection="row" alignItems="center" gap="s">
            <Icon as={ShieldAlert} style={styles.iconWarning} size={24} />
            <CardTitle>March Madness Setup Needed</CardTitle>
          </Box>
          <CardDescription>Tournament data is not imported yet for this season.</CardDescription>
        </CardHeader>
        <CardContent>
          {canManage && (
            <Button
              onPress={async () => {
                const seasonYear = new Date().getFullYear();
                await trpcClient.marchMadness.setup.mutate({
                  ...mmInput,
                  seasonYear,
                  rosterSize: 6,
                  gender: 'mens',
                });
                await invalidateMarchMadnessQueries();
              }}
            >
              Run Setup
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Box flex={1}>
      {activeTab === 'draft' && (
        <ScrollView style={draftScrollStyle} showsVerticalScrollIndicator={false} contentContainerStyle={draftScrollContentStyle}>
          {isDraftActive && (
            <MMClockBanner
              onTheClock={onTheClock}
              displayName={onTheClockDisplayName}
              pickNumber={draftState?.draft?.currentPickNumber ?? 0}
              roundNumber={currentRoundNumber}
            />
          )}

          {!isDraftCompleted && (
            <Card>
              <CardHeader>
                <Box flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="m" flexWrap="wrap">
                  <Box gap="xs" flex={1}>
                    <Box flexDirection="row" alignItems="center" gap="s">
                      <Icon as={Trophy} style={styles.iconPrimary} size={20} />
                      <CardTitle>March Madness Player Pool</CardTitle>
                    </Box>
                    <Box flexDirection="row" gap="s" alignItems="center" flexWrap="wrap">
                      <Badge variant={isDraftActive ? 'default' : isDraftPending ? 'secondary' : 'outline'}>
                        {isDraftPending ? 'DRAFT PENDING' : draftState?.draft?.status?.toUpperCase() ?? 'UNKNOWN'}
                      </Badge>
                      <Text variant="caption" color="mutedForeground">
                        {rosterSize} players • Top 5 cumulative scorers count
                      </Text>
                    </Box>
                    {winnerText && !isDraftPending ? (
                      <Text variant="bodySmall" color="mutedForeground">{winnerText}</Text>
                    ) : null}
                  </Box>
                </Box>
              </CardHeader>
              <CardContent style={draftContentGapStyle}>
                <Box borderWidth={1} borderColor="border" borderRadius="m" padding="m" gap="s">
                  <Text variant="caption" color="mutedForeground" fontWeight="700">DRAFT ORDER</Text>
                  <Box gap="s">
                    {orderedMembers.map((member: any, index: number) => (
                      <Box key={`draft-order-${member.leagueMemberId}`} flexDirection="row" alignItems="center" gap="s">
                        <Box
                          width={28}
                          height={28}
                          borderRadius="l"
                          backgroundColor="muted"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text variant="caption" fontWeight="800">{index + 1}</Text>
                        </Box>
                        <Text variant="bodySmall" fontWeight="700">{member.displayName}</Text>
                      </Box>
                    ))}
                  </Box>
                  <Text variant="caption" color="mutedForeground">Snake draft reverses the order every round.</Text>
                </Box>

                {canManage && (
                  <Box backgroundColor="muted" padding="m" borderRadius="m" gap="m">
                    <Box flexDirection="row" alignItems="center" gap="s">
                      <Icon as={ShieldAlert} style={styles.iconMutedForeground} size={16} />
                      <Text variant="caption" fontWeight="700" color="mutedForeground">MANAGER TOOLS</Text>
                    </Box>

                    <Box flexDirection="row" gap="s" flexWrap="wrap">
                      {isDraftActive ? (
                        <Button
                          size="sm"
                          variant={overrideCurrentPickMode ? 'destructive' : 'outline'}
                          onPress={() => setOverrideCurrentPickMode((previous) => !previous)}
                        >
                          <Text variant="caption" color={overrideCurrentPickMode ? 'textInverse' : 'destructive'}>
                            {overrideCurrentPickMode ? 'Override On' : 'Override Off'}
                          </Text>
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={undoLastPickMutation.isPending || (draftState?.picks?.length ?? 0) === 0}
                        onPress={() => undoLastPickMutation.mutate()}
                      >
                        <Box flexDirection="row" alignItems="center" gap="xs">
                          <Icon as={Undo2} style={styles.iconForeground} size={14} />
                          <Text variant="caption">{undoLastPickMutation.isPending ? 'Undoing...' : 'Undo Last Pick'}</Text>
                        </Box>
                      </Button>
                      {isDraftPending ? (
                        <Button
                          onPress={() => startDraftMutation.mutate()}
                          disabled={startDraftMutation.isPending}
                          backgroundColor={theme.colors.press}
                          borderColor={theme.colors.press}
                        >
                          <Text variant="button">{startDraftMutation.isPending ? 'STARTING...' : 'START DRAFT'}</Text>
                        </Button>
                      ) : null}
                    </Box>

                    <Box gap="s">
                      <Text variant="caption" fontWeight="600">Add missing player to pool</Text>
                      <Box flexDirection="row" gap="s" flexWrap="wrap">
                        <Box minWidth={180} flex={1}>
                          <Input value={manualPlayerName} onChangeText={setManualPlayerName} placeholder="Full Name" />
                        </Box>
                        <Box minWidth={140} flex={1}>
                          <Input value={manualPlayerTeamName} onChangeText={setManualPlayerTeamName} placeholder="Team" />
                        </Box>
                        <Box width={80}>
                          <Input value={manualPlayerPosition} onChangeText={setManualPlayerPosition} placeholder="POS" />
                        </Box>
                        <Button size="sm" disabled={!manualPlayerName.trim() || addManualPlayerMutation.isPending} onPress={() => addManualPlayerMutation.mutate()}>
                          <Icon as={UserPlus} style={styles.iconInverse} size={16} />
                        </Button>
                      </Box>
                    </Box>

                    {isDraftPending ? (
                      <Box gap="s">
                        <Text variant="caption" fontWeight="600">Edit draft order</Text>
                        <Box gap="s">
                          {editableDraftOrderMembers.map((member: any, index: number) => (
                            <Box
                              key={`editable-draft-order-${member.leagueMemberId}`}
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                              gap="s"
                              padding="s"
                              borderRadius="m"
                              backgroundColor="background"
                              borderWidth={1}
                              borderColor="border"
                            >
                              <Box flexDirection="row" alignItems="center" gap="s" flex={1}>
                                <Box
                                  width={28}
                                  height={28}
                                  borderRadius="l"
                                  backgroundColor="muted"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Text variant="caption" fontWeight="800">{index + 1}</Text>
                                </Box>
                                <Text variant="bodySmall" fontWeight="700">{member.displayName}</Text>
                              </Box>
                              <Box flexDirection="row" gap="xs">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  onPress={() => moveDraftOrderMember(member.leagueMemberId, 'up')}
                                >
                                  <Icon as={ArrowUp} style={styles.iconForeground} size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={index === editableDraftOrderMembers.length - 1}
                                  onPress={() => moveDraftOrderMember(member.leagueMemberId, 'down')}
                                >
                                  <Icon as={ArrowDown} style={styles.iconForeground} size={14} />
                                </Button>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Box flexDirection="row" gap="s" flexWrap="wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!isDraftOrderDirty || updateDraftOrderMutation.isPending}
                            onPress={() => setEditableDraftOrder(draftState?.draftOrder ?? [])}
                          >
                            <Text variant="caption">Reset</Text>
                          </Button>
                          <Button
                            size="sm"
                            disabled={!isDraftOrderDirty || updateDraftOrderMutation.isPending}
                            onPress={() => updateDraftOrderMutation.mutate(editableDraftOrder)}
                          >
                            <Text variant="caption" fontWeight="700">
                              {updateDraftOrderMutation.isPending ? 'Saving...' : 'Save Draft Order'}
                            </Text>
                          </Button>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
              <CardHeader style={draftHeaderStyle}>
                <Box flexDirection="row" alignItems="center" gap="s">
                  <Icon as={Users} style={styles.iconPrimary} size={20} />
                  <Text variant="title" fontWeight="700">Draft Board</Text>
                </Box>
              </CardHeader>
              <CardContent style={draftBoardContentStyle}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <Box>
                    <Box flexDirection="row" borderBottomWidth={2} borderColor="border">
                      <Box width={50} paddingVertical="m" alignItems="center" justifyContent="center" backgroundColor="muted">
                        <Text variant="caption" fontWeight="800" color="mutedForeground">RD</Text>
                      </Box>
                      {orderedMembers.map((member: any) => {
                        const isUserMember = member.userId === currentUserId;
                        const isCurrentDrafter = draftState?.draft?.currentDrafterLeagueMemberId === member.leagueMemberId;
                        return (
                          <MMDraftHeader
                            key={`header-${member.leagueMemberId}`}
                            displayName={member.displayName}
                            isUser={isUserMember}
                            isCurrent={isCurrentDrafter}
                            profileImageUrl={member.profileImageUrl}
                          />
                        );
                      })}
                    </Box>

                    {draftRounds.map((roundNumber: any) => (
                      <Box key={`round-${roundNumber}`} flexDirection="row" borderBottomWidth={1} borderColor="border">
                        <Box width={50} alignItems="center" justifyContent="center" backgroundColor="muted">
                          <Text variant="bodySmall" fontWeight="800" color="mutedForeground">{roundNumber}</Text>
                        </Box>
                        {orderedMembers.map((member: any) => {
                          const pick = pickByMemberRound.get(`${member.leagueMemberId}:${roundNumber}`);
                          const isCurrentPickCell =
                            isDraftActive &&
                            currentRoundNumber === roundNumber &&
                            draftState?.draft?.currentDrafterLeagueMemberId === member.leagueMemberId;
                          const currentPickCellStyle = isCurrentPickCell
                            ? {
                                borderLeftWidth: 4,
                                borderLeftColor: theme.colors.acid,
                                backgroundColor: theme.colors.paperDark,
                                shadowColor: theme.colors.press,
                                shadowOffset: { width: 3, height: 3 },
                                shadowOpacity: 1,
                                shadowRadius: 0,
                              }
                            : undefined;
                          return (
                            <Box
                              key={`${member.leagueMemberId}-${roundNumber}`}
                              width={160}
                              minHeight={90}
                              padding="s"
                              borderRightWidth={1}
                              borderColor="border"
                              justifyContent="center"
                              style={currentPickCellStyle}
                            >
                              {pick ? (
                                <Box gap="xs">
                                  <Box flexDirection="row" justifyContent="space-between" alignItems="center">
                                    <Text variant="caption" fontWeight="800" color="accentForeground">#{pick.pickNumber}</Text>
                                    {pick.playerPosition ? (
                                      <Badge variant="outline" paddingHorizontal="xs" paddingVertical="xs">
                                        {pick.playerPosition}
                                      </Badge>
                                    ) : null}
                                  </Box>
                                  <Text variant="bodySmall" fontWeight="700" numberOfLines={2}>{pick.playerName ?? 'Unknown'}</Text>
                                  <Text variant="caption" color="mutedForeground" numberOfLines={1} fontSize={10}>
                                    {pick.playerTeamAbbreviation || pick.playerTeamName || 'No Team'}
                                  </Text>
                                </Box>
                              ) : (
                                <Box alignItems="center" justifyContent="center">
                                  {isCurrentPickCell ? (
                                    <Box alignItems="center" gap="xs">
                                      <ActivityIndicator size="small" color={theme.colors.press} />
                                      <Text variant="caption" color="mutedForeground" fontWeight="700">PICKING...</Text>
                                    </Box>
                                  ) : (
                                    <Text variant="caption" color="muted">RD {roundNumber}</Text>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </ScrollView>
              </CardContent>
            </Card>

            <Card>
              <CardHeader style={playerFinderHeaderStyle}>
                <Box flexDirection="row" alignItems="center" gap="s">
                  <Icon as={Search} style={styles.iconPrimary} size={20} />
                  <Text variant="title" fontWeight="700">Player Finder</Text>
                </Box>
                <Box flexDirection="row" gap="s" marginTop="m" flexWrap="wrap">
                  <Box flex={1} minWidth={220} position="relative">
                    <Box position="absolute" left={12} top={12} zIndex={10}>
                      <Icon as={Search} style={styles.iconMutedForeground} size={16} />
                    </Box>
                    <Input
                      value={playerSearchQuery}
                      onChangeText={setPlayerSearchQuery}
                      placeholder="Search player or team..."
                      style={playerSearchStyle}
                    />
                  </Box>
                  <Box width={160}>
                    <AppSelect value={positionFilter} onChange={setPositionFilter} options={positionOptions} placeholder="Position" />
                  </Box>
                </Box>
              </CardHeader>
                <CardContent style={playerFinderContentStyle}>
                <ScrollView style={playerFinderScrollStyle}>
                  <Box>
                    {filteredPlayers.length === 0 ? (
                      <Box padding="xl" alignItems="center" gap="s">
                        <Icon as={User} style={styles.iconMuted} size={48} />
                        <Text variant="body" color="mutedForeground">No players found matching your search.</Text>
                      </Box>
                    ) : (
                      filteredPlayers.slice(0, 150).map((player: any) => {
                        const drafted = draftedByPlayerId.get(player.id);
                        const canPick = !drafted && isDraftActive && (overrideCurrentPickMode || onTheClock);
                        return (
                          <Box
                            key={player.id}
                            flexDirection="row"
                            alignItems="center"
                            paddingHorizontal="xl"
                            paddingVertical="m"
                            borderBottomWidth={1}
                            borderColor="border"
                            backgroundColor={drafted ? 'muted' : 'background'}
                            opacity={drafted ? 0.7 : 1}
                          >
                            <Box flex={1} gap="xs">
                              <Box flexDirection="row" alignItems="center" gap="s" flexWrap="wrap">
                                <Text variant="body" fontWeight="700" color={drafted ? 'mutedForeground' : 'text'}>{player.fullName}</Text>
                                {player.position ? (
                                  <Badge variant="outline">
                                    <Text fontSize={10} fontWeight="700">{player.position}</Text>
                                  </Badge>
                                ) : null}
                              </Box>
                              <Text variant="caption" color="mutedForeground">
                                {player.teamAbbreviation || player.teamName || 'No Team'}
                              </Text>
                              {drafted ? (
                                <Text variant="caption" fontWeight="600" color="mutedForeground">
                                  Drafted by {drafted.drafterDisplayName} (#{drafted.pickNumber})
                                </Text>
                              ) : null}
                            </Box>

                            <Button
                              size="sm"
                              variant={canPick ? (onTheClock && !overrideCurrentPickMode ? 'default' : 'outline') : 'outline'}
                              disabled={
                                Boolean(drafted) ||
                                !isDraftActive ||
                                (!overrideCurrentPickMode && !onTheClock) ||
                                makePickMutation.isPending ||
                                overridePickMutation.isPending
                              }
                              onPress={() => {
                                if (overrideCurrentPickMode && canManage) {
                                  overridePickMutation.mutate(player.id);
                                } else {
                                  makePickMutation.mutate(player.id);
                                }
                              }}
                              style={canPick && onTheClock && !overrideCurrentPickMode ? { backgroundColor: theme.colors.acid, borderColor: theme.colors.press } : undefined}
                            >
                              {drafted ? (
                                <Text variant="caption" fontWeight="700" color="mutedForeground">DRAFTED</Text>
                              ) : makePickMutation.isPending || overridePickMutation.isPending ? (
                                <ActivityIndicator size="small" color={theme.colors.press} />
                              ) : (
                                <Box flexDirection="row" alignItems="center" gap="xs">
                                  <Text
                                    variant="caption"
                                    fontWeight="800"
                                    color={canPick ? 'accentForeground' : 'mutedForeground'}
                                  >
                                    {overrideCurrentPickMode && canManage ? 'OVERRIDE' : 'DRAFT'}
                                  </Text>
                                  {canPick ? (
                                    <Icon
                                      as={ChevronRight}
                                      style={styles.iconPrimary}
                                      size={14}
                                    />
                                  ) : null}
                                </Box>
                              )}
                            </Button>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </ScrollView>
              </CardContent>
            </Card>
        </ScrollView>
      )}

      {activeTab === 'bracket' && (
        <ScrollView style={bracketScrollStyle} showsVerticalScrollIndicator={false}>
          {bracket ? (
            <MarchMadnessBracketView
              bracket={bracket}
              compactRoundKey={compactBracketRoundKey}
              onSelectCompactRound={setCompactBracketRoundKey}
            />
          ) : (
            <Card>
              <CardContent style={bracketLoadingStyle}>
                <ActivityIndicator size="small" color={theme.colors.press} />
                <Text variant="bodySmall" color="mutedForeground" marginTop="m">
                  Loading live bracket...
                </Text>
              </CardContent>
            </Card>
          )}
        </ScrollView>
      )}

      {activeTab === 'standings' && (
        <Box flex={1} gap="none">
          {/* Standings List (Scrollable) */}
          <ScrollView
            style={standingsScrollStyle}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={standingsContentStyle}
          >
            {overview?.standings?.map((entry: any) => {
              const progress = (entry.totalPoints / maxTournamentPoints) * 100;
              return (
                <Box
                  key={entry.leagueMemberId}
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  padding="m"
                  borderRadius="none"
                  backgroundColor="surface"
                  borderWidth={1}
                  borderColor={entry.rank === 1 ? 'accentForeground' : 'border'}
                  style={entry.rank === 1 ? winningStandingStyle : undefined}
                >
                  <Box flexDirection="row" alignItems="center" gap="m" flex={1}>
                    <Box position="relative">
                      <Avatar size={40}>
                        {entry.profileImageUrl ? (
                          <>
                            <AvatarImage src={entry.profileImageUrl} />
                            <AvatarFallback>
                              <GradientAvatarFallback name={entry.displayName}>
                                {entry.displayName.substring(0, 1).toUpperCase()}
                              </GradientAvatarFallback>
                            </AvatarFallback>
                          </>
                        ) : (
                          <GradientAvatarFallback name={entry.displayName}>
                            {entry.displayName.substring(0, 1).toUpperCase()}
                          </GradientAvatarFallback>
                        )}
                      </Avatar>
                      {entry.rank <= 3 && (
                        <Box position="absolute" bottom={-10} right={-10} style={[{ transform: [{ scale: 0.65 }] }]}>
                           <MMRankBadge rank={entry.rank} />
                        </Box>
                      )}
                    </Box>
                    <Box flex={1}>
                      <Text variant="body" fontWeight="800" color={entry.rank === 1 ? 'accentForeground' : 'text'}>{entry.displayName}</Text>
                      <Box height={4} backgroundColor="muted" borderRadius="none" marginTop="xs" overflow="hidden" width="80%">
                         <Box height="100%" backgroundColor={entry.rank === 1 ? 'accent' : 'mutedForeground'} width={`${progress}%`} />
                      </Box>
                    </Box>
                  </Box>
                  <Box alignItems="flex-end">
                    <Text variant="title" fontWeight="900">{entry.totalPoints}</Text>
                    <Text variant="caption" color="mutedForeground">PTS</Text>
                  </Box>
                </Box>
              );
            })}
          </ScrollView>

          {/* Madness Climber Visual (Pinned to Bottom) */}
          <Box style={[styles.scoreRaceContent, scoreRaceContentStyle]}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.scoreRaceScrollContent}
            >
              <Box style={[styles.scoreRaceChartCard, { width: scoreRaceChart.chartWidth }]}>
                <Box style={styles.scoreRaceChartLayer}>
                  <Svg width={scoreRaceChart.chartWidth} height={scoreRaceChart.chartHeight}>
                      <Defs>
                        {scoreRaceChart.series.map((entry: any) => {
                          const endPoint = entry.points[entry.points.length - 1];
                          if (!endPoint) return null;
                          const endPointX =
                            endPoint.x + (scoreRaceChart.endpointOffsets.get(entry.leagueMemberId) ?? 0);

                          return (
                            <ClipPath key={`clip-${entry.leagueMemberId}`} id={`score-race-avatar-${entry.leagueMemberId}`}>
                              <Circle cx={endPointX} cy={endPoint.y} r={11} />
                            </ClipPath>
                          );
                        })}
                      </Defs>

                      {/* Main Background - Sky */}
                      <Rect
                        x={0}
                        y={0}
                        width={scoreRaceChart.chartWidth}
                        height={scoreRaceChart.chartHeight}
                        fill={theme.colors.paper}
                      />

                      {/* Subtle Court Grid */}
                      {scoreRaceChart.gridValues.map((value: any) => {
                        const y = scoreRaceChart.yForValue(value);
                        return (
                          <Line
                            key={`court-h-${value}`}
                            x1={scoreRaceChart.horizontalPadding}
                            y1={y}
                            x2={scoreRaceChart.chartWidth - scoreRaceChart.horizontalPadding}
                            y2={y}
                            stroke={COURT_LINE_COLOR}
                            strokeWidth={1}
                          />
                        );
                      })}

                      {/* Mountain Peaks (Increasing in height left-to-right with more detail) */}
                      {(() => {
                        const championshipX = scoreRaceChart.roundBandBounds[scoreRaceChart.roundBandBounds.length - 1]?.centerX ?? scoreRaceChart.chartWidth;
                        const h = scoreRaceChart.chartHeight;
                        
                        // Peak 1 Geometry
                        const p1v = { x: championshipX * 0.7, y: h * 0.55 };
                        const p1bL = { x: championshipX * 0.3, y: h };
                        const p1bR = { x: championshipX * 1.1, y: h };
                        
                        // Peak 2 Geometry (Championship)
                        const p2v = { x: championshipX, y: h * 0.35 };
                        const p2bL = { x: championshipX * 0.6, y: h };
                        const p2bR = { x: scoreRaceChart.chartWidth, y: h };

                        // Helper to find point on line (vertex to base) at given Y
                        const getX = (v: any, b: any, y: number) => v.x + (b.x - v.x) * ((y - v.y) / (b.y - v.y));

                        const s1y = h * 0.62;
                        const s1L = getX(p1v, p1bL, s1y);
                        const s1R = getX(p1v, p1bR, s1y);

                        const s2y = h * 0.46;
                        const s2L = getX(p2v, p2bL, s2y);
                        const s2R = getX(p2v, p2bR, s2y);

                        return (
                          <React.Fragment>
                            {/* Peak 1 */}
                            <Path
                              d={`M ${p1bL.x} ${p1bL.y} L ${p1v.x} ${p1v.y} L ${p1bR.x} ${p1bR.y} Z`}
                              fill={MOUNTAIN_GRAY_DARK}
                              opacity={0.2}
                            />
                            {/* Peak 2 */}
                            <Path
                              d={`M ${p2bL.x} ${p2bL.y} L ${p2v.x} ${p2v.y} L ${p2bR.x} ${p2bR.y} Z`}
                              fill={MOUNTAIN_GRAY_DARK}
                              opacity={0.25}
                            />

                            {/* Ridges on Main Peak */}
                            <Path
                              d={`M ${p2v.x} ${p2v.y} L ${p2v.x - 10} ${h * 0.55} L ${p2v.x + 20} ${h * 0.8} Z`}
                              fill={theme.colors.paperDark}
                            />

                            {/* Snow Cap 1 */}
                            <Path
                              d={`M ${s1L} ${s1y} L ${p1v.x} ${p1v.y} L ${s1R} ${s1y} L ${p1v.x + 8} ${h * 0.6} L ${p1v.x} ${h * 0.63} L ${p1v.x - 8} ${h * 0.6} Z`}
                              fill={theme.colors.acid}
                              stroke={theme.colors.press}
                              strokeWidth={0.5}
                              opacity={0.9}
                            />
                            {/* Snow Cap 2 */}
                            <Path
                              d={`M ${s2L} ${s2y} L ${p2v.x} ${p2v.y} L ${s2R} ${s2y} L ${p2v.x + 12} ${h * 0.43} L ${p2v.x} ${h * 0.47} L ${p2v.x - 12} ${h * 0.43} Z`}
                              fill={theme.colors.acid}
                              stroke={theme.colors.press}
                              strokeWidth={0.5}
                              opacity={1}
                            />
                          </React.Fragment>
                        );
                      })()}

                      {/* Axis Labels and Grid */}
                      {scoreRaceChart.gridValues.map((value: any) => {
                        const y = scoreRaceChart.yForValue(value);
                        return (
                          <SvgText
                            key={`label-${value}`}
                            x={scoreRaceChart.horizontalPadding - 10}
                            y={y + 4}
                            fill="rgba(15,23,42,0.4)"
                            fontSize="11"
                            fontWeight="800"
                            textAnchor="end"
                          >
                            {value}
                          </SvgText>
                        );
                      })}

                      {/* Series Fills (Area) */}
                      {scoreRaceChart.series.map((entry: any) => (
                        <Path
                          key={`fill-${entry.leagueMemberId}`}
                          d={entry.fillPath}
                          fill={`url(#area-grad-${entry.leagueMemberId})`}
                        />
                      ))}

                      {/* Round Labels (Integrated into SVG) */}
                      {scoreRaceChart.roundBandBounds.map((band: any) => (
                        <SvgText
                          key={`round-text-${band.key}`}
                          x={band.centerX}
                          y={scoreRaceChart.chartHeight - 12}
                          fill="rgba(15,23,42,0.85)"
                          fontSize="11"
                          fontWeight="900"
                          fontFamily="System"
                          textAnchor="middle"
                        >
                          {((overview?.rounds ?? []).find((round: any) => round.roundKey === band.key)?.label ?? band.key).toUpperCase()}
                        </SvgText>
                      ))}

                      {/* Series Paths (Lines) */}
                      {scoreRaceChart.series
                        .slice()
                        .reverse()
                        .map((entry: any) => (
                          <Path
                            key={entry.leagueMemberId}
                            d={entry.path}
                            fill="none"
                            stroke={entry.color}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeOpacity={entry.rank <= 3 ? 1 : 0.6}
                            strokeWidth={entry.rank <= 3 ? 4 : 2}
                          />
                        ))}

                      {/* Basketball Markers */}
                      {scoreRaceChart.series.map((entry: any) =>
                        entry.points.map((point: any, index: number) => (
                          <MMBasketballMarker
                            key={`${entry.leagueMemberId}-${point.key}`}
                            cx={point.x}
                            cy={point.y}
                            size={index === entry.points.length - 1 ? 14 : 10}
                          />
                        )),
                      )}

                      {/* Final Hoop (MAX Size & High detail, Aligned with Championship) */}
                      {(() => {
                         const championshipX = scoreRaceChart.roundBandBounds[scoreRaceChart.roundBandBounds.length - 1]?.centerX ?? scoreRaceChart.chartWidth;
                         return (
                           <React.Fragment>
                              <Rect
                                x={championshipX - 30}
                                y={scoreRaceChart.topPadding - 15}
                                width={60}
                                height={45}
                                rx={6}
                                fill="rgba(255,255,255,0.3)"
                                stroke="rgba(15,23,42,0.15)"
                                strokeWidth={2}
                              />
                              {/* Backboard Inner Rectangle */}
                              <Rect
                                x={championshipX - 15}
                                y={scoreRaceChart.topPadding + 5}
                                width={30}
                                height={20}
                                stroke="rgba(15,23,42,0.2)"
                                strokeWidth={1}
                                fill="none"
                              />
                              <Circle
                                cx={championshipX}
                                cy={scoreRaceChart.topPadding + 22}
                                r={20}
                                stroke="#FF6B00"
                                strokeWidth={4}
                                fill="none"
                              />
                              {/* Detailed Large Net */}
                              <Path
                                d={`M ${championshipX - 20} ${scoreRaceChart.topPadding + 22} 
                                  L ${championshipX - 14} ${scoreRaceChart.topPadding + 52}
                                  L ${championshipX + 14} ${scoreRaceChart.topPadding + 52}
                                  L ${championshipX + 20} ${scoreRaceChart.topPadding + 22}
                                  
                                  M ${championshipX - 20} ${scoreRaceChart.topPadding + 22}
                                  L ${championshipX} ${scoreRaceChart.topPadding + 58}
                                  L ${championshipX + 20} ${scoreRaceChart.topPadding + 22}
                                  
                                  M ${championshipX - 10} ${scoreRaceChart.topPadding + 22}
                                  L ${championshipX + 10} ${scoreRaceChart.topPadding + 52}
                                  M ${championshipX + 10} ${scoreRaceChart.topPadding + 22}
                                  L ${championshipX - 10} ${scoreRaceChart.topPadding + 52}
                                  `}
                                stroke="rgba(15,23,42,0.25)"
                                strokeWidth={1.5}
                                fill="none"
                              />
                           </React.Fragment>
                         );
                      })()}
                      {scoreRaceChart.series.map((entry: any) => {
                        const endPoint = entry.points[entry.points.length - 1];
                        if (!endPoint) return null;
                        const endPointX =
                          endPoint.x + (scoreRaceChart.endpointOffsets.get(entry.leagueMemberId) ?? 0);

                        return (
                          <React.Fragment key={`avatar-${entry.leagueMemberId}`}>
                            {/* Leader Glow */}
                            {entry.rank === 1 && (
                              <Circle
                                cx={endPointX}
                                cy={endPoint.y}
                                r={22}
                                fill={entry.color}
                                opacity={0.25}
                              />
                            )}
                            
                            <Circle
                              cx={endPointX}
                              cy={endPoint.y}
                              r={15}
                              fill={theme.colors.surface}
                              stroke={entry.color}
                              strokeWidth={3}
                            />
                            {entry.profileImageUrl ? (
                              <SvgImage
                                x={endPointX - 11}
                                y={endPoint.y - 11}
                                width={22}
                                height={22}
                                href={resolveAvatarUri(entry.profileImageUrl)}
                                clipPath={`url(#score-race-avatar-${entry.leagueMemberId})`}
                                preserveAspectRatio="xMidYMid slice"
                              />
                            ) : (
                              <>
                                <Circle cx={endPointX} cy={endPoint.y} r={11} fill={entry.color} />
                                <SvgText
                                  x={endPointX}
                                  y={endPoint.y + 3}
                                  fill={theme.colors.white}
                                  fontSize="8"
                                  fontWeight="900"
                                  textAnchor="middle"
                                >
                                  {getInitials(entry.displayName)}
                                </SvgText>
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Svg>

                    {scoreRaceChart.series.map((entry: any) => {
                      return entry.points.map((point: any, index: number) => {
                        const isEndpoint = index === entry.points.length - 1;
                        const horizontalOffset = isEndpoint
                          ? scoreRaceChart.endpointOffsets.get(entry.leagueMemberId) ?? 0
                          : 0;

                        const tooltipContent = (
                          <Box style={styles.scoreRaceTooltipContent}>
                            <Text variant="bodySmall" fontWeight="800" style={styles.scoreRaceTooltipName}>
                              {entry.displayName}
                            </Text>
                            <Text variant="caption" style={styles.scoreRaceTooltipValue}>
                              {point.cumulativeScore} points
                            </Text>
                          </Box>
                        );

                        return (
                          <Box
                            key={`tooltip-target-${entry.leagueMemberId}-${point.key}`}
                            style={styles.scoreRaceEndpointHotspot(
                              point.x + horizontalOffset,
                              point.y,
                            )}
                          >
                            <Tooltip content={tooltipContent} width={160}>
                              <Box style={styles.scoreRaceEndpointTouchTarget} />
                            </Tooltip>
                          </Box>
                        );
                      });
                    })}
                  </Box>
                </Box>
              </ScrollView>
          </Box>
        </Box>
      )}

      {activeTab === 'rosters' && (
        <ScrollView style={rostersScrollStyle} showsVerticalScrollIndicator={false} contentContainerStyle={rostersScrollContentStyle}>
          <Box gap="m">
              <Box style={styles.roundSelector}>
                {roundSelectorRows.map((row: any[], rowIndex: number) => (
                  <Box key={`row-${rowIndex}`} style={styles.roundSelectorRow}>
                    {row.map((round: any) => (
                    <Button
                      key={round.roundKey}
                      size="sm"
                      variant={selectedRoundKey === round.roundKey ? 'default' : 'outline'}
                      style={styles.roundSelectorButton}
                      onPress={() => {
                        router.replace(buildMmHref(routeSection, { mmRound: round.roundKey }));
                      }}
                    >
                      <Text variant="caption" fontWeight="700" style={styles.roundSelectorButtonText} numberOfLines={1}>
                        {round.label}
                      </Text>
                    </Button>
                    ))}
                  </Box>
                ))}
              </Box>
              {!selectedRoundDetail && context.isLoading ? (
                <ActivityIndicator size="small" color={theme.colors.press} />
              ) : (
                selectedRoundDetail?.standings?.map((entry: any) => (
                  <Box key={entry.leagueMemberId} borderWidth={1} borderColor="border" borderRadius="m" padding="m" gap="s">
                    <Box
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      paddingHorizontal="s"
                    >
                      <Box flex={1}>
                        <Text variant="body" fontWeight="700">{entry.displayName}</Text>
                      </Box>
                      <Box style={styles.summaryPill}>
                        <Box style={styles.summaryPillRound}>
                          <Text variant="caption" fontWeight="800" style={styles.summaryPillRoundLabel}>Round</Text>
                          <Text variant="bodySmall" fontWeight="900" style={styles.summaryPillRoundValue}>{entry.roundTotalPoints}</Text>
                        </Box>
                        <Box style={styles.summaryPillDivider} />
                        <Box style={styles.summaryPillTournament}>
                          <Text variant="caption" fontWeight="800" style={styles.summaryPillTournamentLabel}>Tourney</Text>
                          <Text variant="bodySmall" fontWeight="800" style={styles.summaryPillTournamentValue}>{entry.runningTotalPoints}</Text>
                        </Box>
                      </Box>
                    </Box>
                    {entry.roster.map((player: any) => {
                      const cumulativePoints =
                        player.cumulativePoints ??
                        cumulativePointsByMemberPlayer.get(`${entry.leagueMemberId}:${player.playerId}`) ??
                        0;
                      const roundScoreLabel =
                        player.isEliminated
                          ? 'ELIM'
                          : player.points === 0 && !player.hasPlayedInRound
                            ? 'TBD'
                            : String(player.points);
                      const eliminatedBadgeStyle = {
                        borderColor: theme.colors.muted,
                        paddingHorizontal: 4,
                        paddingVertical: 0,
                        height: 16,
                      };
                      const scorePillStateStyles = [
                        player.isEliminated ? { backgroundColor: theme.colors.muted } : undefined,
                        player.isLive ? { backgroundColor: theme.colors.success } : undefined,
                      ];
                      const scorePillTextStyle = player.isEliminated ? { fontStyle: 'italic' as const } : undefined;

                      return (
                        <Box
                          key={player.playerId}
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="s"
                          padding="s"
                          borderRadius="m"
                          backgroundColor={player.counted ? 'surface' : 'muted'}
                          style={player.counted ? styles.countedPlayerRow : undefined}
                        >
                          <Box flex={1} gap="xs">
                            <Box flexDirection="row" alignItems="center" gap="xs" flexWrap="wrap">
                              {player.teamLogoUrl ? (
                                <Image source={{ uri: player.teamLogoUrl }} style={styles.playerTeamLogo} resizeMode="contain" />
                              ) : null}
                              <Text variant="bodySmall" fontWeight="600">{player.fullName}</Text>
                              {player.isLive && (
                                <LiveIndicator label="LIVE" />
                              )}
                              {player.isEliminated && (
                                <Badge variant="outline" style={eliminatedBadgeStyle}>
                                  <Text variant="caption" color="mutedForeground" fontWeight="800" fontSize={8}>ELIM</Text>
                                </Badge>
                              )}
                              {!player.counted ? <Badge variant="outline">Dropped</Badge> : null}
                            </Box>
                            <Text variant="caption" color="mutedForeground">
                              #{player.pickNumber} • {player.teamAbbreviation || player.teamName || 'No Team'}
                            </Text>
                          </Box>
                          <Box style={styles.scorePill}>
                            <Box style={[styles.scorePillCurrent, scorePillStateStyles]}>
                              <Text
                                variant="bodySmall"
                                fontWeight="900"
                                color={player.isLive ? 'successForeground' : 'accentForeground'}
                                style={[styles.scorePillCurrentText, scorePillTextStyle]}
                              >
                                {roundScoreLabel}
                              </Text>
                            </Box>
                            <Box style={styles.scorePillDivider} />
                            <Box style={styles.scorePillCumulative}>
                              <Text variant="bodySmall" fontWeight="700" style={[styles.cumulativePointsValue, styles.scorePillCumulativeText]}>
                                {String(cumulativePoints)}
                              </Text>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ))
              )}
          </Box>
        </ScrollView>
      )}

      {canManage && activeTab === 'admin' && (
        <ScrollView style={rostersScrollStyle} showsVerticalScrollIndicator={false} contentContainerStyle={rostersScrollContentStyle}>
          <Box gap="m">
            <Card>
              <CardHeader>
                <Box flexDirection="row" alignItems="center" gap="s">
                  <Icon as={Sparkles} style={styles.iconPrimary} size={20} />
                  <CardTitle>Round Recap Prompt</CardTitle>
                </Box>
                <CardDescription>Generate an AI-ready recap prompt for a completed round.</CardDescription>
              </CardHeader>
              <CardContent style={adminContentGapStyle}>
                <Box flexDirection="row" gap="s" flexWrap="wrap">
                  <Box minWidth={220} flex={1}>
                    <Text variant="caption" color="mutedForeground" marginBottom="xs">Round</Text>
                    <AppSelect
                      value={selectedRoundKey ?? ''}
                      onChange={(value) => {
                        router.replace(buildMmHref(routeSection, { mmRound: value }));
                      }}
                      options={(overview?.rounds ?? []).map((round: any) => ({
                        value: round.roundKey,
                        label: round.label,
                      }))}
                      placeholder="Choose round"
                    />
                  </Box>
                  <Box minWidth={180} flex={1}>
                    <Text variant="caption" color="mutedForeground" marginBottom="xs">Tone</Text>
                    <AppSelect
                      value={recapTone}
                      onChange={(value) =>
                        setRecapTone(
                          value === 'competitive' || value === 'chaotic' || value === 'professional' ? value : 'fun',
                        )
                      }
                      options={[
                        { value: 'fun', label: 'Fun' },
                        { value: 'competitive', label: 'Competitive' },
                        { value: 'chaotic', label: 'Chaotic' },
                        { value: 'professional', label: 'Professional' },
                      ]}
                      placeholder="Choose tone"
                    />
                  </Box>
                </Box>

                <Box flexDirection="row" gap="s" flexWrap="wrap">
                  <Button
                    onPress={() => generateRecapPromptMutation.mutate()}
                    disabled={!selectedRoundKey || generateRecapPromptMutation.isPending}
                  >
                    <Box flexDirection="row" alignItems="center" gap="xs">
                      <Icon as={Sparkles} style={styles.iconInverse} size={14} />
                      <Text variant="caption" fontWeight="700">
                        {generateRecapPromptMutation.isPending ? 'Generating...' : 'Generate Prompt'}
                      </Text>
                    </Box>
                  </Button>
                  <Button variant="outline" onPress={copyRecapPrompt} disabled={!recapPrompt.trim()}>
                    <Box flexDirection="row" alignItems="center" gap="xs">
                      <Icon as={Copy} style={styles.iconForeground} size={14} />
                      <Text variant="caption" fontWeight="700">Copy Prompt</Text>
                    </Box>
                  </Button>
                </Box>

                <Textarea
                  value={recapPrompt}
                  onChangeText={setRecapPrompt}
                  placeholder="Prompt output will appear here..."
                  style={recapPromptStyle}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Box flexDirection="row" alignItems="center" gap="s">
                  <Icon as={Bell} style={styles.iconPrimary} size={20} />
                  <CardTitle>Manual Push Notification</CardTitle>
                </Box>
                <CardDescription>Send a manual league-wide push for this March Madness season.</CardDescription>
              </CardHeader>
              <CardContent style={adminContentGapStyle}>
                <Box>
                  <Text variant="caption" color="mutedForeground" marginBottom="xs">Preset</Text>
                  <AppSelect
                    value={notificationPreset}
                    onChange={handleNotificationPresetChange}
                    options={notificationPresetOptions.map((option: any) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    placeholder="Choose preset"
                  />
                </Box>
                <Box>
                  <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom="xs">
                    <Text variant="caption" color="mutedForeground">Title</Text>
                    <Text variant="caption" color="mutedForeground">
                      {notificationTitle.length}/{notificationTitleLimit}
                    </Text>
                  </Box>
                  <Input
                    value={notificationTitle}
                    onChangeText={setNotificationTitle}
                    placeholder="League Update"
                    maxLength={notificationTitleLimit}
                  />
                </Box>
                <Box>
                  <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom="xs">
                    <Text variant="caption" color="mutedForeground">Message</Text>
                    <Text variant="caption" color="mutedForeground">
                      {notificationMessage.length}/{notificationMessageLimit}
                    </Text>
                  </Box>
                  <Textarea
                    value={notificationMessage}
                    onChangeText={setNotificationMessage}
                    placeholder="Write your message..."
                    maxLength={notificationMessageLimit}
                  />
                </Box>
                <Text variant="caption" color="mutedForeground">
                  Manual pushes go to all league members with valid push tokens.
                </Text>
                <Button
                  onPress={() => sendNotificationMutation.mutate()}
                  disabled={!notificationMessage.trim() || sendNotificationMutation.isPending}
                >
                  <Box flexDirection="row" alignItems="center" gap="xs">
                    <Icon as={Bell} style={styles.iconInverse} size={14} />
                    <Text variant="caption" fontWeight="700">
                      {sendNotificationMutation.isPending ? 'Sending...' : 'Send Notification'}
                    </Text>
                  </Box>
                </Button>
              </CardContent>
            </Card>
          </Box>
        </ScrollView>
      )}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  countedPlayerRow: {
    borderLeftColor: theme.colors.press,
    borderLeftWidth: 3,
  },
  cumulativePointsValue: {
    color: theme.colors.accentForeground,
    opacity: 0.95,
  },
  iconForeground: {
    color: theme.colors.text,
  },
  iconInverse: {
    color: theme.colors.textInverse,
  },
  iconMuted: {
    color: theme.colors.muted,
  },
  iconMutedForeground: {
    color: theme.colors.mutedForeground,
  },
  iconPrimary: {
    color: theme.colors.accentForeground,
  },
  iconWarning: {
    color: theme.colors.warning,
  },
  playerTeamLogo: {
    height: 18,
    width: 18,
  },
  roundSelector: {
    gap: theme.spacing.s,
  },
  roundSelectorButton: {
    flex: 1,
    minHeight: 44,
    minWidth: 0,
    paddingHorizontal: theme.spacing.xs,
  },
  roundSelectorButtonText: {
    flexShrink: 1,
    fontSize: 12,
    textAlign: 'center',
    width: '100%',
  },
  roundSelectorRow: {
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  scorePill: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.border,
    borderRadius: 0,
    borderWidth: 1,
    flexDirection: 'row',
    flexShrink: 0,
    maxWidth: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
    minWidth: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
    overflow: 'hidden',
    width: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
  },
  scorePillCumulative: {
    alignItems: 'center',
    backgroundColor: theme.colors.acid,
    justifyContent: 'center',
    minHeight: 36,
    width: {
      xs: 69,
      sm: MM_SCORE_PILL_SECONDARY_WIDTH,
    },
  },
  scorePillCumulativeText: {
    color: theme.colors.accentForeground,
    fontSize: {
      xs: 14,
      sm: 16,
    },
    fontWeight: '800',
  },
  scorePillCurrent: {
    alignItems: 'center',
    backgroundColor: theme.colors.paperDark,
    justifyContent: 'center',
    minHeight: 36,
    width: {
      xs: 70,
      sm: MM_SCORE_PILL_PRIMARY_WIDTH,
    },
  },
  scorePillCurrentText: {
    fontSize: {
      xs: 14,
      sm: 16,
    },
  },
  scorePillDivider: {
    backgroundColor: theme.colors.border,
    height: '100%',
    width: 1,
  },
  scoreRaceChartCard: {
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadii.xl,
    borderWidth: 1,
    padding: theme.spacing.s,
  },
  scoreRaceChartLayer: {
    position: 'relative',
  },
  scoreRaceContent: {
    gap: theme.spacing.m,
  },
  scoreRaceEndpointHotspot: (x: number, y: number) => ({
    left: x - 18,
    position: 'absolute',
    top: y - 18,
    zIndex: 5,
  }),
  scoreRaceEndpointTouchTarget: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    height: 36,
    width: 36,
  },
  scoreRaceScrollContent: {
    paddingBottom: theme.spacing.xs,
  },
  scoreRaceTooltipContent: {
    alignItems: 'center',
    gap: 2,
  },
  scoreRaceTooltipName: {
    color: theme.colors.white,
    textAlign: 'center',
  },
  scoreRaceTooltipValue: {
    color: 'rgba(249,250,251,0.84)',
    textAlign: 'center',
  },
  summaryPill: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.border,
    borderRadius: 0,
    borderWidth: 1,
    flexDirection: 'row',
    flexShrink: 0,
    maxWidth: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
    minWidth: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
    overflow: 'hidden',
    width: {
      xs: 140,
      sm: MM_SCORE_PILL_WIDTH,
    },
  },
  summaryPillDivider: {
    backgroundColor: theme.colors.border,
    height: '100%',
    width: 1,
  },
  summaryPillRound: {
    alignItems: 'center',
    backgroundColor: theme.colors.press,
    justifyContent: 'center',
    minHeight: 46,
    width: {
      xs: 70,
      sm: MM_SCORE_PILL_PRIMARY_WIDTH,
    },
  },
  summaryPillRoundLabel: {
    color: theme.colors.textInverse,
    fontSize: {
      xs: 11,
      sm: 13,
    },
    opacity: 0.9,
  },
  summaryPillRoundValue: {
    color: theme.colors.textInverse,
    fontSize: {
      xs: 14,
      sm: 16,
    },
  },
  summaryPillTournament: {
    alignItems: 'center',
    backgroundColor: theme.colors.acid,
    justifyContent: 'center',
    minHeight: 46,
    width: {
      xs: 69,
      sm: MM_SCORE_PILL_SECONDARY_WIDTH,
    },
  },
  summaryPillTournamentLabel: {
    color: theme.colors.accentForeground,
    fontSize: {
      xs: 11,
      sm: 13,
    },
    opacity: 0.9,
  },
  summaryPillTournamentValue: {
    color: theme.colors.accentForeground,
    fontSize: {
      xs: 14,
      sm: 16,
    },
    fontWeight: '900',
  },
}));
